import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    IconButton,
    Typography,
    Grid,
    Menu,
    MenuItem,
} from "@mui/material";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useStyles } from "./ProductionLineAccordion.styles";
import {
    IMachineCenterSelected,
    IMachineCenterToDelete,
    IMachineCenterToDuplicate,
    IProductionLineAccordion,
} from "./ProductionLineAccordionInterfaces";
import { ModalProductionLines } from "../ModalProductionLines";
import { MachineCenterCreationForm } from "../../forms/MachineCenterCreationForm/MachineCenterCreationForm";
import { IMachineCenterCreationFormValues } from "../../forms/MachineCenterCreationForm/MachineCenterCreationFormInterfaces";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import useRolesPermissions from "hooks/useRolesPermissions";
import { CORPORATE_BACKOFFICE_PATHS, DOWNTIMEMETHODCAPTURE } from "constants/constants";
import {
    DndContext,
    closestCenter,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { SortableMachineCenterCard } from "./SortableMachineCenterCard";
import { useNavigate, useParams } from "react-router-dom";
import { MachineCenterCard } from "../MachineCenterCard";
import { ModalClusters } from "pages/Corporate/CorporateSetupDashboard/Clusters/components";
import { ClustersCreationForm } from "pages/Corporate/CorporateSetupDashboard/Clusters/forms/ClustersCreationForm";
import { ICluster } from "entities/ClusterEntity/ClusterEntityInterfaces";
import moment from "moment";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";
import { IMachineCenterFailureDetailsInfoBO } from "entities/MachineCenters/MachineCentersInterfaces";
import { ProductionLinesCreationForm } from "../../forms";

export const ProductionLineAccordion = ({ productionLine, refetch, setProductionLines }: IProductionLineAccordion) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const navigate = useNavigate();
    const routeParams = useParams();
    const [openProductionLineEdition, setOpenProductionLineEdition] = useState(false);
    const [expand, setExpand] = useState<boolean>(true);
    const [open, setOpen] = useState<boolean>(false);
    const [clusterEditModalOpen, setClusterEditModalOpen] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState(false);
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor)); // Sensors for DND Kit
    const [editableCluster, setEditableCluster] = useState<ICluster | null>(null);
    const [openDeleteMachineCenter, setOpenDeleteMachineCenter] = useState(false);
    const [machineCenterToDelete, setMachineCenterToDelete] = useState<IMachineCenterToDelete | null>(null);
    const [machineCenterToDuplicate, setMachineCenterToDuplicate] = useState<IMachineCenterToDuplicate | undefined>(
        undefined,
    );
    const [openMachineCenterDuplicate, setOpenMachineCenterDuplicate] = useState<boolean>(false);
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const [anchorElForActionMenu, setAnchorElForActionMenu] = useState<null | HTMLElement>(null);
    const millId = useMemo(() => Number(routeParams?.millId) || 0, [routeParams?.millId]);

    const { apolloMutationError, addMachineCenter } = MachineCentersEntity.InsertMachineCentersCorporateBO();
    const { upsertOeeSettings, upsertOeeProducts } = MachineCentersEntity.UpsertMachineCenterOeeSettings();
    const { setMachineCentersOrder } = MachineCentersEntity.SetMachineCentersOrderByProductionLine();
    const { updateMachineCenterMutation } = MachineCentersEntity.UpdateMachineCenterCorporateBO();
    const { validateOngoingEvent, validateExistingEvents } = MachineCentersEntity.CheckMachineCenterOngoingEvent();

    const handleOnClickDelete = (machineCenter: IMachineCenterToDelete) => {
        setOpenDeleteMachineCenter(true);
        setMachineCenterToDelete(machineCenter);
    };

    const handleOnClickDuplicate = useCallback(
        (machineCenter: IMachineCenterSelected) => {
            const duplicationDetails = {
                productionLineId: productionLine.production_line_id,
                machineCenterId: machineCenter.machineCenterId,
                name: machineCenter.name,
                productionLineOptions: productionLine.productionLineOptions,
            };
            setMachineCenterToDuplicate(duplicationDetails);
            setOpenMachineCenterDuplicate(true);
        },
        [productionLine],
    );

    const handleDeleteMachineCenter = useCallback(async () => {
        try {
            if (machineCenterToDelete) {
                const noOngoingDowntimeEvent = await validateOngoingEvent(machineCenterToDelete.id);
                const hasAnEvent = await validateExistingEvents(machineCenterToDelete.id);
                if (noOngoingDowntimeEvent) {
                    const machineCenterValues = {
                        disabled_at: moment.utc().endOf("day").toDate(),
                        ...(!hasAnEvent && { deleted_at: moment.utc().endOf("day").toDate() }),
                    };
                    const response = await updateMachineCenterMutation({
                        variables: { machineCenterId: machineCenterToDelete.id, changes: machineCenterValues },
                    });
                    if (response) {
                        await refetch();
                    } else {
                        throw Error("No response");
                    }
                }
            }
        } catch (error) {
            throw Error(JSON.stringify(error));
        }
    }, [machineCenterToDelete, updateMachineCenterMutation, validateOngoingEvent, validateExistingEvents, refetch]);

    const handleClusterEditModalClose = useCallback(async () => {
        setClusterEditModalOpen(false);
        setEditableCluster(null);
        await refetch();
    }, [refetch]);

    const handleClusterEditModalOpen = () => setClusterEditModalOpen(true);

    const handleOpen = () => setOpen(true);

    const handleClose = useCallback(async () => {
        await refetch();
        setOpen(false);
        setOpenMachineCenterDuplicate(false);
        setMachineCenterToDuplicate(undefined);
    }, [setOpen, refetch]);

    const handleCloseEditionModal = useCallback(async () => {
        await refetch();
        setOpenProductionLineEdition(false);
    }, [refetch, setOpenProductionLineEdition]);

    const handleSubmit = useCallback(
        async (values: IMachineCenterCreationFormValues, duplicationValues?: IMachineCenterFailureDetailsInfoBO) => {
            const differentProductionLineSelected =
                values?.production_line_id_send_duplicate !== productionLine.production_line_id &&
                values.is_duplication_with_selection;
            let lastOrderNumber = Math.max(
                productionLine.machine_centers.length + 1,
                productionLine.highestOrderNumber + 1,
            );
            if (differentProductionLineSelected) {
                lastOrderNumber =
                    productionLine.productionLineOptions.find(
                        (plOption) => plOption.id === Number(values.production_line_id_send_duplicate),
                    )?.highestOrderNumber! + 1;
            }
            const machineCenterValues = {
                name: values.name,
                short_name: values.short_name,
                ...(values.manufacturer && { manufacturer: values.manufacturer }),
                ...(values.model_number && { model_number: values.model_number }),
                mill_id: productionLine.mill.mill_id,
                production_line_id: values.is_duplication_with_selection
                    ? values.production_line_id_send_duplicate
                    : productionLine.production_line_id,
                downtime_method_capture_id: values.downtime_method_capture_id,
                ...(values.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC && {
                    plc_address: values.plc_address,
                }),
                ...(values.minimum_downtime && { minimum_downtime: Number(values.minimum_downtime) }),
                ...(values.downtime_trigger_threshold && {
                    downtime_trigger_threshold: Number(values.downtime_trigger_threshold),
                }),
                is_bottleneck: values.is_bottleneck,
                order: lastOrderNumber,
                ...(Boolean(duplicationValues?.non_location_specific_machine_center_failure_codes?.length) && {
                    non_location_specific_machine_center_failure_codes: {
                        data: duplicationValues?.non_location_specific_machine_center_failure_codes?.map((fc) => ({
                            failure_code_id: fc.failure_code_id,
                        })),
                    },
                }),
                ...(Boolean(duplicationValues?.areas?.length) && {
                    areas: {
                        data: duplicationValues?.areas.map((area) => ({
                            name_translation_id: area.name_translation_id,
                            ...(Boolean(area?.functional_locations?.length) && {
                                functional_locations: {
                                    data: area?.functional_locations.map((fl) => ({
                                        name_translation_id: fl.name_translation_id,
                                        ...(Boolean(
                                            fl?.location_specific_functional_location_failure_codes?.length,
                                        ) && {
                                            location_specific_functional_location_failure_codes: {
                                                data: fl?.location_specific_functional_location_failure_codes.map(
                                                    (fc) => ({ failure_code_id: fc.failure_code_id }),
                                                ),
                                            },
                                        }),
                                    })),
                                },
                            }),
                        })),
                    },
                }),
            };
            try {
                if (values.is_bottleneck)
                    await MachineCentersEntity.desvinculateMachineCenterBottleneck(productionLine.production_line_id);
                const response = await addMachineCenter({ variables: { machineCenter: machineCenterValues } });
                const newMachineCenterId = response?.data?.insert_machine_center_one?.machine_center_id;
                if (response && newMachineCenterId != null) {
                    await upsertOeeSettings(
                        newMachineCenterId,
                        values.aspen_plus_oee_enabled,
                        values.oee_input_method_id === "" ? undefined : values.oee_input_method_id,
                        {
                            record_quality_enabled: values.record_quality_enabled,
                            quality_manual_entry: values.quality_manual_entry,
                            quality_calculation_method_id:
                                values.quality_calculation_method_id === ""
                                    ? undefined
                                    : values.quality_calculation_method_id,
                        },
                    );
                    if (values.aspen_plus_oee_enabled && values.oee_products.length > 0) {
                        const productsInput = values.oee_products.map((p) => ({
                            name: p.name,
                            machine_center_id: newMachineCenterId,
                            product_ideal_values: {
                                data: p.product_ideal_values.map((v) => ({ value: v.value })),
                            },
                        }));
                        await upsertOeeProducts(productsInput);
                    }
                    await handleClose();
                }
            } catch {
                return;
            }
        },
        [addMachineCenter, handleClose, productionLine, upsertOeeSettings, upsertOeeProducts],
    );

    const handleDragMove = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            const { active, over } = event;
            setIsDragging(false);

            if (active.id !== over?.id) {
                const oldIndex = productionLine.machine_centers.findIndex(
                    (machineCenter) => machineCenter.machine_center_id.toString() === active.id,
                );
                const newIndex = productionLine.machine_centers.findIndex(
                    (machineCenter) => machineCenter.machine_center_id.toString() === over?.id,
                );

                const newMachineCenters = arrayMove(productionLine.machine_centers, oldIndex, newIndex);
                setProductionLines((prevProductionLines) => {
                    const productionLineIndex = prevProductionLines.findIndex(
                        (pl) => pl.production_line_id === productionLine.production_line_id,
                    );
                    const newProductionLines = [...prevProductionLines];
                    newProductionLines[productionLineIndex] = {
                        ...newProductionLines[productionLineIndex],
                        machine_centers: newMachineCenters,
                    };
                    return newProductionLines;
                });

                try {
                    // Update with null order to avoid conflict on same order by production line constraint
                    await setMachineCentersOrder({
                        variables: {
                            machineCenters: newMachineCenters.map((machineCenter, index) => {
                                return {
                                    machine_center_id: machineCenter.machine_center_id,
                                    order: null,
                                    name: machineCenter.name,
                                    production_line_id: productionLine.production_line_id,
                                    downtime_method_capture_id:
                                        machineCenter.downtime_method_capture.downtime_method_capture_id,
                                    is_bottleneck: machineCenter.is_bottleneck,
                                    mill_id: productionLine.mill.mill_id,
                                };
                            }),
                        },
                    });
                    // Update with new ordering
                    await setMachineCentersOrder({
                        variables: {
                            machineCenters: newMachineCenters.map((machineCenter, index) => {
                                const orderNumber = index + 1;
                                return {
                                    machine_center_id: machineCenter.machine_center_id,
                                    order: orderNumber,
                                    name: machineCenter.name,
                                    production_line_id: productionLine.production_line_id,
                                    downtime_method_capture_id:
                                        machineCenter.downtime_method_capture.downtime_method_capture_id,
                                    is_bottleneck: machineCenter.is_bottleneck,
                                    mill_id: productionLine.mill.mill_id,
                                };
                            }),
                        },
                    });
                    await refetch();
                } catch (error) {
                    console.error("Failed to update machine centers order", error);
                }
            }
        },
        [productionLine, refetch, setMachineCentersOrder, setProductionLines],
    );

    const handleCardOnClick = useCallback(
        (machineCenterId: number) => {
            !isDragging &&
                navigate(
                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.MACHINE_CENTER_DETAILS.ROOT}/${machineCenterId}`,
                );
        },
        [isDragging, navigate],
    );

    return (
        <Accordion
            expanded={expand}
            onChange={(event, expanded) => {
                setExpand(expanded);
            }}
            className={classes.accordion}
        >
            <Box className={classes.accordionSummaryContainer}>
                <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
                    aria-controls={`panel${productionLine.production_line_id}a-content`}
                    id={`panel${productionLine.production_line_id}a-header`}
                >
                    <Typography className={classes.accordionSummaryTitle}>{productionLine.name}</Typography>
                </AccordionSummary>
                {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditProductionLines && (
                    <Box>
                        <Button className={classes.addMachineButton} variant="text" onClick={handleOpen}>
                            <AddCircleIcon /> {t("corporate_backoffice.production_line_view.add_new_machine_button")}
                        </Button>
                        <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={Boolean(anchorElForActionMenu) ? "long-menu" : undefined}
                            aria-expanded={Boolean(anchorElForActionMenu) ? "true" : undefined}
                            aria-haspopup="true"
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                setAnchorElForActionMenu(event.currentTarget);
                            }}
                        >
                            <MoreHorizIcon className={classes.moreIcon} />
                        </IconButton>
                        <Menu
                            id="long-menu"
                            MenuListProps={{
                                "aria-labelledby": "long-button",
                            }}
                            anchorEl={anchorElForActionMenu}
                            open={Boolean(anchorElForActionMenu)}
                            onClose={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                setAnchorElForActionMenu(null);
                            }}
                        >
                            <MenuItem
                                onClick={(event: React.MouseEvent<HTMLElement>) => {
                                    event.stopPropagation();
                                    setAnchorElForActionMenu(null);
                                    setOpenProductionLineEdition(true);
                                }}
                            >
                                {t(
                                    "corporate_backoffice.production_line_view.production_line_creation_form.edit_option",
                                )}
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Box>
            <AccordionDetails className={classes.accordionDetails}>
                {CORPORATE_PERMISSIONS.corporationPermissions.canDragAndDropMachineCenters ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragMove={handleDragMove}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={productionLine.machine_centers.map((item) => item.machine_center_id.toString())}
                            strategy={rectSortingStrategy}
                        >
                            <Grid container spacing={6}>
                                {productionLine.machine_centers.map((machineCenter) => (
                                    <SortableMachineCenterCard
                                        handleOnClickDelete={handleOnClickDelete}
                                        handleOnClickDuplicate={handleOnClickDuplicate}
                                        key={machineCenter.machine_center_id}
                                        id={machineCenter.machine_center_id.toString()}
                                        machineCenter={machineCenter}
                                        handleCardOnClick={() => handleCardOnClick(machineCenter.machine_center_id)}
                                        handleClusterEditModalOpen={handleClusterEditModalOpen}
                                        setEditableCluster={setEditableCluster}
                                        refetch={refetch}
                                    />
                                ))}
                            </Grid>
                        </SortableContext>
                    </DndContext>
                ) : (
                    <Grid container spacing={6}>
                        {productionLine.machine_centers.map((machineCenter) => (
                            <Grid item xs={3} key={machineCenter.machine_center_id}>
                                <MachineCenterCard
                                    handleCardOnClick={() => handleCardOnClick(machineCenter.machine_center_id)}
                                    handleOnClickDelete={handleOnClickDelete}
                                    handleOnClickDuplicate={handleOnClickDuplicate}
                                    machineCenter={machineCenter}
                                    handleClusterEditModalOpen={handleClusterEditModalOpen}
                                    setEditableCluster={setEditableCluster}
                                    refetch={refetch}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </AccordionDetails>
            <ModalProductionLines
                open={open || openMachineCenterDuplicate}
                handleClose={handleClose}
                alignTop
                title={
                    openMachineCenterDuplicate && machineCenterToDuplicate
                        ? t(
                              "corporate_backoffice.production_line_view.machine_center_creation_form.modal_title_with_duplication",
                              { name: machineCenterToDuplicate?.name },
                          )
                        : t("corporate_backoffice.production_line_view.machine_center_creation_form.modal_title")
                }
            >
                <MachineCenterCreationForm
                    duplicateMachineCenterInfo={machineCenterToDuplicate}
                    handleClose={handleClose}
                    handleSubmit={handleSubmit}
                    apolloMutationError={apolloMutationError}
                    millId={productionLine.mill.mill_id}
                    isMillAspenPlusEnabled={productionLine.mill.mill_modules?.[0]?.is_enabled ?? false}
                />
            </ModalProductionLines>
            <ModalClusters
                open={clusterEditModalOpen}
                handleClose={handleClose}
                title={
                    editableCluster
                        ? t("corporate_backoffice.cluster_view.cluster_creation_form.modal_edit_cluster_title")
                        : t("corporate_backoffice.cluster_view.cluster_creation_form.modal_create_cluster_title")
                }
            >
                <ClustersCreationForm
                    handleClose={handleClusterEditModalClose}
                    millId={productionLine.mill.mill_id}
                    cluster={editableCluster}
                    clustersRefetch={refetch}
                />
            </ModalClusters>
            <CheckUserPasswordModal
                open={openDeleteMachineCenter}
                onConfirmPassword={handleDeleteMachineCenter}
                onClose={() => {
                    setMachineCenterToDelete(null);
                    setOpenDeleteMachineCenter(false);
                }}
                title={t("corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.header")}
                question={t(
                    "corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.confirm_modal_question",
                    { name: machineCenterToDelete?.name },
                )}
                questionConfirmedButtonText={t(
                    "corporate_backoffice.production_line_view.machine_center_card.delete_machine_center.header",
                )}
            />
            <ModalProductionLines
                open={openProductionLineEdition}
                handleClose={handleCloseEditionModal}
                title={t("corporate_backoffice.production_line_view.production_line_creation_form.modal_edit_title", {
                    name: productionLine.name,
                })}
            >
                <ProductionLinesCreationForm
                    handleClose={handleCloseEditionModal}
                    millId={millId}
                    productionLineEdition={productionLine}
                />
            </ModalProductionLines>
        </Accordion>
    );
};
